/**
 * ResponsiveTabs
 *
 * MUI <Tabs> with the two things every tabbed page in the portal needs:
 *
 *  1. it scrolls instead of squashing or overflowing (`scrollable` at every width, with arrows
 *     that only appear when the set does not fit — a nine-tab page overflows a desktop too),
 *  2. the active tab lives in the URL (`?tab=<key>`), so a tab is linkable, survives a refresh,
 *     a refresh keeps it. Switching tabs REPLACES the history entry rather than pushing one, so
 *     Back leaves the page instead of walking back through every tab you looked at.
 *
 * Tabs are addressed by string key, never by index, so adding or role-gating a tab cannot shift
 * what `?tab=` points at.
 *
 *   const [tab, setTab] = useState('open')
 *   <ResponsiveTabs tabs={TABS} value={tab} onChange={setTab} />
 */

import { ReactElement, ReactNode, useEffect } from 'react'
import { Box, Tab, Tabs } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { useSearchParams } from 'react-router-dom'

export interface ResponsiveTabItem {
  key: string
  label: string
  icon?: ReactNode
  /** A count badge after the label — "Pending 12". Omitted or undefined draws nothing; 0 draws 0. */
  count?: number
  disabled?: boolean
}

export interface ResponsiveTabsProps {
  tabs: ResponsiveTabItem[]
  /** The active tab's `key`. */
  value: string
  onChange: (key: string) => void
  /** Query-string parameter the active tab syncs to. Default: "tab". */
  paramName?: string
  sx?: SxProps<Theme>
}

export function ResponsiveTabs({ tabs, value, onChange, paramName = 'tab', sx }: ResponsiveTabsProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const paramValue = searchParams.get(paramName)

  // URL -> state. Covers the first render (a deep link) and the Back button.
  //
  // This is the ONLY effect. There used to be a second one writing state -> URL, and the two
  // fought on mount: this one asked the page to adopt the URL's tab, and in the same commit the
  // other saw the page's still-default state and wrote THAT into the URL first. Every deep link
  // — /badges?tab=achievements, the /tickets/statistics redirect — landed on the first tab with
  // the URL rewritten to match. The URL is written from the click handler below instead, so the
  // only thing that ever changes it is a person choosing a tab.
  useEffect(() => {
    if (!paramValue || paramValue === value) return
    if (!tabs.some((t) => t.key === paramValue)) return
    onChange(paramValue)
  }, [paramValue, value, tabs, onChange])

  // `replace` so switching tabs does not stack history entries the user has to click back through.
  const select = (key: string) => {
    if (searchParams.get(paramName) !== key) {
      const next = new URLSearchParams(searchParams)
      next.set(paramName, key)
      setSearchParams(next, { replace: true })
    }
    onChange(key)
  }

  // A key that is not (yet) in the list — a role-gated tab, a stale URL — would make MUI warn
  // about an out-of-range value, so fall back to the first tab.
  const safeValue = tabs.some((t) => t.key === value) ? value : (tabs[0]?.key ?? false)

  return (
    <Tabs
      value={safeValue}
      onChange={(_, key: string) => select(key)}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        minHeight: 44,
        '& .MuiTab-root': {
          minHeight: 44,
          minWidth: { xs: 'auto', md: 90 },
          px: { xs: 1.5, sm: 2 },
          fontSize: { xs: '0.8125rem', sm: '0.875rem' },
        },
        ...sx,
      }}
    >
      {tabs.map((tab) => (
        <Tab
          key={tab.key}
          value={tab.key}
          disabled={tab.disabled}
          label={
            tab.count === undefined ? (
              tab.label
            ) : (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                {tab.label}
                <Box
                  component="span"
                  sx={{
                    px: 0.75,
                    minWidth: 20,
                    borderRadius: 10,
                    fontSize: '0.75rem',
                    lineHeight: '20px',
                    textAlign: 'center',
                    bgcolor: tab.count > 0 ? 'primary.main' : 'action.selected',
                    color: tab.count > 0 ? 'primary.contrastText' : 'text.secondary',
                  }}
                >
                  {tab.count}
                </Box>
              </Box>
            )
          }
          // MUI types `icon` as ReactElement; the prop is ReactNode here so callers can pass
          // anything, and an undefined icon simply renders no icon.
          icon={tab.icon as ReactElement | undefined}
          iconPosition="start"
        />
      ))}
    </Tabs>
  )
}
